import React, {useMemo} from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';

import {CURRENCY} from '@invoice/core/constants/currency';

import {I18N_NS} from '../../core/constants/names';
import {$allowedActionsForInvoice} from '../../models/common';

import {$isNG} from '../../models/utils';
import {HeaderGroupItem, RequiredTag} from '../Pages/InvoiceLines/styled';

export type ColumnNames = Record<string, {id: string; label: string}>;

export type InvoiceLinesColumnSetting = {
    id: string;
    header: React.ReactNode;
    width: number;
    innerContainerStyles?: string;
};

export function useInvoiceLinesColumns(): {
    columnNames: ColumnNames;
    columnsSettings: InvoiceLinesColumnSetting[];
} {
    const {t: translation} = useTranslation(I18N_NS);
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);

    return useMemo(() => {
        const columnNames: ColumnNames = {
            lineId: {id: 'lineId', label: ''},
            itemCode: {id: 'itemCode', label: t('itemCode')},
            description: {id: 'description', label: t('productName')},
            serviceCategory: {id: 'serviceCategory', label: t('serviceCategory')},
            qty: {id: 'qty', label: t('qty')},
            price: {id: 'price', label: t('price')},
            charges: {id: 'charges', label: t('charges')},
            lines: {id: 'lines', label: t('lines')},
            vatCategory: {id: 'vatCategory', label: t('vatCategory')},
            vat: {id: 'vat', label: t('vat')},
            lineAmountWVat: {
                id: 'lineAmountWVat',
                label: t('lineAmountWVat', {currency: CURRENCY}),
            },
            control: {id: 'control', label: ''},
        };

        const columnsSettings: InvoiceLinesColumnSetting[] = [
            {id: columnNames.lineId.id, header: columnNames.lineId.label, width: 48},
            {
                id: columnNames.itemCode.id,
                header: (
                    <div>
                        <HeaderGroupItem>
                            {t('itemCode')} {<RequiredTag>*</RequiredTag>}
                        </HeaderGroupItem>
                    </div>
                ),
                width: 180 + 32,
            },
            {
                id: columnNames.description.id,
                header: (
                    <div>
                        <HeaderGroupItem>
                            {t('nameOfProduct')} {<RequiredTag>*</RequiredTag>}
                        </HeaderGroupItem>
                        <div>{t('description')}</div>
                    </div>
                ),
                width: 180 + 32,
            },
            {
                id: columnNames.serviceCategory.id,
                header: (
                    <HeaderGroupItem>
                        {columnNames.serviceCategory.label}{' '}
                        {$isNG.getState() && <RequiredTag>*</RequiredTag>}
                    </HeaderGroupItem>
                ),
                width: 174 + 32,
            },
            {
                id: columnNames.qty.id,
                header: <HeaderGroupItem>QTY {<RequiredTag>*</RequiredTag>}</HeaderGroupItem>,
                width: 52 + 32,
            },
            {
                id: columnNames.price.id,
                header: (
                    <HeaderGroupItem>
                        {columnNames.price.label} ({CURRENCY}) {<RequiredTag>*</RequiredTag>}
                    </HeaderGroupItem>
                ),
                width: 90 + 32,
            },
            {id: columnNames.charges.id, header: 'Charges', width: 160 + 32},
            {
                id: columnNames.lines.id,
                header: `${columnNames.lines.label} (${CURRENCY})`,
                width: 100 + 32,
            },
            {
                id: columnNames.vatCategory.id,
                header: (
                    <HeaderGroupItem>
                        {columnNames.vatCategory.label} {<RequiredTag>*</RequiredTag>}
                    </HeaderGroupItem>
                ),
                width: 118 + 32,
            },
            {id: columnNames.vat.id, header: columnNames.vat.label, width: 60 + 32},
            {
                id: columnNames.lineAmountWVat.id,
                header: columnNames.lineAmountWVat.label,
                width: 100 + 32,
            },
            ...(allowedActionsForInvoice.edit
                ? [{id: columnNames.control.id, header: '', width: 50}]
                : []),
        ];

        return {columnNames, columnsSettings};
    }, [t, allowedActionsForInvoice.edit]);
}
